import {
  EditorPlatformHostIntegrationAPI,
  IEditorPlatformPackagesBridge,
} from '@wix/editor-platform-host-integration-apis';
import {
  PlatformEventEmitterAPI,
  EditorPlatformElementsPanelAPI,
  EditorPlatformEditTextPanelAPI,
} from '@wix/editor-platform-feature-store/internal';
import { EntryPoint, SlotKey } from 'repluggable';
import {
  EditorPointerServiceAPI,
  PresetsPanelUIServiceAPI,
} from '@wix/editor-platform-feature-store';
import { BaseKey } from '@wix/edicore-base';

type BridgeEntrypoints = {
  [K in keyof IEditorPlatformPackagesBridge]: SlotKey<
    IEditorPlatformPackagesBridge[K]
  >;
};

// Note that if you add a new entrypoint - the host editor needs to initialize it in the EditorPlatformLauncherEntryPointer (studio and odeditor)
// for it to be available here.
// TODO: remove partial and add edit text panel after merging new infra panel

const bridgeEntrypointsSource: BridgeEntrypoints = {
  PlatformEventsEmitter: PlatformEventEmitterAPI,
  PlatformElementsPanelAPI: EditorPlatformElementsPanelAPI,
  PlatformEditTextPanelAPI: EditorPlatformEditTextPanelAPI,
  PresetsPanelUIServiceAPI,
  EditorPointerAPI: EditorPointerServiceAPI,
};

const platformPackagesCoreEntrypoints: BaseKey<unknown>[] = [
  PlatformEventEmitterAPI,
];

const getBridgeEntrypoint = <K extends keyof IEditorPlatformPackagesBridge>(
  name: K,
  key: BridgeEntrypoints[K],
): EntryPoint => ({
  name: `${key.name}-bridge`,

  getDependencyAPIs: () => [EditorPlatformHostIntegrationAPI, key],

  extend(shell) {
    const hostIntegration = shell.getAPI(EditorPlatformHostIntegrationAPI);
    const contributionApi = shell.getAPI(key);

    if (!contributionApi) {
      throw new Error(`Contribution API ${key} not found`);
    }

    hostIntegration.contributeEditorPlatformPackagesBridge({
      [name]: contributionApi,
    });
  },
});

// prepare the entrypoints that are not initialized yet
const initializedBridgeKeys = Object.values(bridgeEntrypointsSource).reduce(
  (acc, key) => {
    acc[key.name] = false;
    return acc;
  },
  {} as Record<string, boolean>,
);

export const getInitializedBridgeEntrypoints = async (
  getInitializedServices: () => BaseKey<unknown>[],
  onInitialized: (entrypoints: EntryPoint[]) => void,
) => {
  // NOTE: This iteration based logic is temporary until we implement
  // events whenever a service is initialized.
  // TODO: https://wix.atlassian.net/browse/EP-6726

  // try initializing for up to 3 minutes.
  const timeout = 1000 * 60 * 3;

  // check if initialized once every 2 seconds
  const interval = 2000;

  const startTime = Date.now();

  const initializedEntrypointsNames = new Set<string>();

  while (
    initializedEntrypointsNames.size <
      Object.keys(initializedBridgeKeys).length &&
    Date.now() - startTime < timeout
  ) {
    const initializedServices = getInitializedServices();

    const newEntrypoints = Object.entries(bridgeEntrypointsSource)
      .filter(
        ([_, key]) =>
          initializedServices.find((sKey) => sKey.name === key.name) ||
          platformPackagesCoreEntrypoints.find(
            (sKey) => sKey.name === key.name,
          ),
      )
      .map(([name, key]) =>
        getBridgeEntrypoint(name as keyof IEditorPlatformPackagesBridge, key),
      );

    const notInitializedEntrypoints = newEntrypoints.filter(
      (entrypoint) => !initializedEntrypointsNames.has(entrypoint.name),
    );

    onInitialized(notInitializedEntrypoints);

    notInitializedEntrypoints.forEach((entrypoint) => {
      initializedEntrypointsNames.add(entrypoint.name);
    });

    await new Promise((resolve) => setTimeout(resolve, interval));
  }
};
